import {
  Box,
  Card,
  Text,
  VStack,
  Flex,
  Image,
  useDisclosure,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  Button,
  Icon,
  Stack,
} from '@chakra-ui/react';
import CustomButton from '@/components/common/Button';
import { usePostStoreDetails, useStoreDetails } from '@/services/store-settings';
import CustomModal from '@/components/common/CustomModal';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';
import { useState, useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomInput from '@/components/common/Input';
import useCustomToast from '@/components/common/Toast';
import { AxiosError } from 'axios';
import { AddressFieldWithMap } from '@/components/common/AddressFieldWithMap';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import BannerLogo from '@/assets/png/shop-banner.png';
import ImageIcon from '@/assets/svg/image-upload.svg?react';
import Skeleton from '@/components/common/Skeleton';

type IStoreFormData = {
  shop_name: string;
  warehouse_location?: string;
  description?: string;
  shop_image?: File | string | null;
  shop_cover_image?: File | string | null;
};

const FILE_SIZE_LIMIT = 1 * 1024 * 1024;
const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];

const validationSchema = yup.object().shape({
  shop_name: yup
    .string()
    .required('Store name is required')
    .min(2, 'Store name must be at least 2 characters')
    .max(100, 'Store name cannot exceed 100 characters'),
  warehouse_location: yup
    .string()
    .optional()
    .max(200, 'Shop location cannot exceed 200 characters'),
  description: yup.string().optional().max(500, 'Description cannot exceed 500 characters'),
  shop_image: yup
    .mixed()
    .nullable()
    .optional()
    .test('fileSize', 'Image file size must be less than 1MB', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) return value.size <= FILE_SIZE_LIMIT;
      return true;
    })
    .test('fileType', 'Only JPG, PNG, WEBP images are allowed', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) return SUPPORTED_TYPES.includes(value.type);
      return true;
    }),
  shop_cover_image: yup
    .mixed()
    .nullable()
    .optional()
    .test('fileSize', 'Image file size must be less than 1MB', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) return value.size <= FILE_SIZE_LIMIT;
      return true;
    })
    .test('fileType', 'Only JPG, PNG, WEBP images are allowed', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) return SUPPORTED_TYPES.includes(value.type);
      return true;
    }),
}) as yup.ObjectSchema<IStoreFormData>;
const cleanValidationErrors = (errorMessage: string): string => {
  const sentences = errorMessage.split('.');
  const uniqueSentences = [];
  const seenSentences = new Set();
  for (const sentence of sentences) {
    const cleanSentence = sentence.trim();
    if (cleanSentence && !seenSentences.has(cleanSentence.toLowerCase())) {
      seenSentences.add(cleanSentence.toLowerCase());
      uniqueSentences.push(cleanSentence);
    }
  }
  return uniqueSentences.join('. ').trim();
};

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

const formatValidationError = (error: AxiosError<ApiErrorResponse> | string): string => {
  if (typeof error === 'string') return cleanValidationErrors(error);
  if (error.response?.data?.error) return cleanValidationErrors(error.response.data.error);
  if (error.response?.data?.message) return cleanValidationErrors(error.response.data.message);
  return 'An unexpected error occurred. Please try again.';
};

interface StoreSettingsProps {
  selectedTabIndex: number;
}

const StoreSettings: React.FC<StoreSettingsProps> = ({ selectedTabIndex }) => {
  const { data: storeDetails, isLoading, isError } = useStoreDetails();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isLoaded: isGoogleMapsLoaded, error: googleMapsError } = useGoogleMaps(
    selectedTabIndex === 1,
  );
  const { showToast } = useCustomToast();

  const [localStoreData, setLocalStoreData] = useState({
    shop_name: '',
    description: '',
    warehouse_location: '',
    shop_image: '',
    shop_cover_image: '',
  });

  const methods = useForm<IStoreFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      shop_name: '',
      warehouse_location: '',
      description: '',
      shop_image: null,
      shop_cover_image: null,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = methods;

  const [shopImagePreview, setShopImagePreview] = useState<string | null>(null);
  const [shopCoverImagePreview, setShopCoverImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (storeDetails) {
      setLocalStoreData({
        shop_name: storeDetails.shop_name || '',
        description: storeDetails.description || '',
        warehouse_location: storeDetails.warehouse_location || '',
        shop_image: storeDetails.shop_image || '',
        shop_cover_image: storeDetails.shop_cover_image || '',
      });
    }
  }, [storeDetails]);

  useEffect(() => {
    if (googleMapsError) showToast('error', googleMapsError);
  }, [googleMapsError, showToast]);

  const shopImage = watch('shop_image');
  const shopCoverImage = watch('shop_cover_image');

  const handleEdit = () => {
    reset({
      shop_name: localStoreData.shop_name,
      warehouse_location: localStoreData.warehouse_location,
      description: localStoreData.description,
      shop_image: null,
      shop_cover_image: null,
    });
    setShopImagePreview(null);
    setShopCoverImagePreview(null);
    onOpen();
  };

  const handleCancel = () => {
    reset();
    setShopImagePreview(null);
    setShopCoverImagePreview(null);
    onClose();
  };

  const { mutateAsync: updateStoreDetails, isLoading: updateIsLoading } = usePostStoreDetails();

  const onSubmit = async (data: IStoreFormData) => {
    const formData = new FormData();
    let hasChanges = false;

    if (data.shop_name && data.shop_name !== localStoreData.shop_name) {
      formData.append('shop_name', data.shop_name);
      hasChanges = true;
    }

    if (data.warehouse_location !== localStoreData.warehouse_location) {
      formData.append('warehouse_location', data.warehouse_location || '');
      hasChanges = true;
    }

    if (data.description !== localStoreData.description) {
      formData.append('description', data.description || '');
      hasChanges = true;
    }

    if (data.shop_image instanceof File) {
      formData.append('shop_image', data.shop_image);
      hasChanges = true;
    } else if (data.shop_image === '' && localStoreData.shop_image) {
      formData.append('shop_image', '');
      hasChanges = true;
    }

    if (data.shop_cover_image instanceof File) {
      formData.append('shop_cover_image', data.shop_cover_image);
      hasChanges = true;
    } else if (data.shop_cover_image === '' && localStoreData.shop_cover_image) {
      formData.append('shop_cover_image', '');
      hasChanges = true;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      const response = await updateStoreDetails(formData);

      setLocalStoreData({
        shop_name: response.data.shop_name || '',
        description: response.data.description || '',
        warehouse_location: response.data.warehouse_location || '',
        shop_image: response.data.shop_image || '',
        shop_cover_image: response.data.shop_cover_image || '',
      });

      const cleanMessage = cleanValidationErrors(
        response.message || 'Store details updated successfully',
      );
      showToast('success', cleanMessage);
      setShopImagePreview(null);
      setShopCoverImagePreview(null);
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = formatValidationError(error);
        showToast('error', errorMessage);
      } else {
        showToast('error', 'An unexpected error occurred');
      }
    }
  };

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof IStoreFormData,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_SIZE_LIMIT) {
      showToast('error', 'Maximum file size is 1MB');
      return;
    }
    if (!SUPPORTED_TYPES.includes(file.type)) {
      showToast('error', 'Only JPG, PNG, WEBP images are allowed');
      return;
    }
    setValue(fieldName, file);

    if (fieldName === 'shop_image') {
      setShopImagePreview(URL.createObjectURL(file));
    } else if (fieldName === 'shop_cover_image') {
      setShopCoverImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    fieldName: keyof IStoreFormData,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setValue(fieldName, '');
    if (fieldName === 'shop_image') {
      setShopImagePreview(null);
    } else if (fieldName === 'shop_cover_image') {
      setShopCoverImagePreview(null);
    }
  };

  const displayShopImage = isError
    ? 'https://placehold.co/150x150'
    : localStoreData.shop_image || 'https://placehold.co/150x150';

  const displayCoverImage = isError ? BannerLogo : localStoreData.shop_cover_image || BannerLogo;

  return (
    <>
      <CustomModal
        title="Edit Store Details"
        isOpen={isOpen}
        onClose={onClose}
        footerContent={
          <HStack width={'full'} justify={'space-between'}>
            <CustomButton variant="outline" onClick={handleCancel}>
              Cancel
            </CustomButton>
            <CustomButton isLoading={updateIsLoading} onClick={handleSubmit(onSubmit)}>
              Update
            </CustomButton>
          </HStack>
        }
      >
        <FormProvider {...methods}>
          <VStack align={'start'} spacing={6}>
            <FormControl isInvalid={!!errors.shop_name}>
              <FormLabel color={errors.shop_name ? 'status.error' : 'text.secondary'}>
                Store Name <Asterisk />
              </FormLabel>
              <Controller
                name="shop_name"
                control={control}
                render={({ field }) => <CustomInput {...field} placeholder="Enter store name" />}
              />
              <FormErrorMessage>{errors.shop_name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.warehouse_location}>
              <FormLabel color={errors.warehouse_location ? 'status.error' : 'text.secondary'}>
                Shop Location
              </FormLabel>
              <AddressFieldWithMap
                name="warehouse_location"
                isGoogleMapsLoaded={isGoogleMapsLoaded}
              />
              <FormErrorMessage>{errors.warehouse_location?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.description}>
              <FormLabel color={errors.description ? 'status.error' : 'text.secondary'}>
                Description
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value || ''}
                    fontFamily={'primary'}
                    bg={'background.white'}
                    placeholder="Enter store description"
                  />
                )}
              />
              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>

            <Stack direction={['column', 'row']} width={'full'} spacing={4} align={'start'}>
              <FormControl isInvalid={!!errors.shop_image}>
                <FormLabel
                  color={errors.shop_image ? 'status.error' : 'text.secondary'}
                  fontSize={'sm'}
                  fontFamily={'primary'}
                  fontWeight={'normal'}
                  mb={'6px'}
                >
                  Upload Store Logo
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={errors.shop_image ? 'status.error' : 'gray.300'}
                  borderRadius="md"
                  overflow={'hidden'}
                  textAlign="center"
                  cursor="pointer"
                  position="relative"
                  onClick={() => document.getElementById('shop_image_input')?.click()}
                >
                  {shopImage && shopImage instanceof File ? (
                    <Box position="relative" bg={'background.white'} className="group">
                      <Button
                        display={'none'}
                        pos={'absolute'}
                        size={'sm'}
                        fontSize={'sm'}
                        right={'50%'}
                        top={'50%'}
                        transform={'translate(50%, -50%)'}
                        color={'text.white'}
                        bg={'status.error'}
                        onClick={(e) => handleRemoveImage(e, 'shop_image')}
                        _groupHover={{ display: 'block' }}
                        zIndex={1}
                      >
                        Remove
                      </Button>
                      <Image
                        src={shopImagePreview || ''}
                        alt="Shop Logo"
                        width={['100%', '400px']}
                        height={['150px', '200px']}
                        objectFit={'cover'}
                        objectPosition={'center'}
                      />
                    </Box>
                  ) : localStoreData.shop_image && shopImage !== '' ? (
                    <Box position="relative" bg={'background.white'} className="group">
                      <Button
                        display={'none'}
                        pos={'absolute'}
                        size={'sm'}
                        fontSize={'sm'}
                        right={'50%'}
                        top={'50%'}
                        transform={'translate(50%, -50%)'}
                        color={'text.white'}
                        bg={'status.error'}
                        onClick={(e) => handleRemoveImage(e, 'shop_image')}
                        _groupHover={{ display: 'block' }}
                        zIndex={1}
                      >
                        Remove
                      </Button>
                      <Image
                        src={localStoreData.shop_image}
                        alt="Shop Logo"
                        width={['100%', '400px']}
                        height={['150px', '200px']}
                        objectFit={'cover'}
                        objectPosition={'center'}
                      />
                    </Box>
                  ) : (
                    <Flex
                      bg={'background.white'}
                      direction={'column'}
                      gap={1}
                      align={'center'}
                      p={4}
                      minH={'150px'}
                      justify={'center'}
                    >
                      <Icon as={ImageIcon} w={8} h={8} color="gray.500" />
                      <Text
                        textAlign={'center'}
                        mt={2}
                        color="brand.primary.DEFAULT"
                        textDecor={'underline'}
                      >
                        Click to upload
                      </Text>
                      <Text textAlign={'center'} fontSize="sm" color="gray.400">
                        PNG, JPG, WEBP up to 1MB
                      </Text>
                    </Flex>
                  )}
                </Box>
                <Input
                  id="shop_image_input"
                  type="file"
                  display="none"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleImageChange(e, 'shop_image')}
                />
                <FormErrorMessage>{errors.shop_image?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.shop_cover_image}>
                <FormLabel
                  color={errors.shop_cover_image ? 'status.error' : 'text.secondary'}
                  fontSize={'sm'}
                  fontFamily={'primary'}
                  fontWeight={'normal'}
                  mb={'6px'}
                >
                  Upload Cover Photo
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={errors.shop_cover_image ? 'status.error' : 'gray.300'}
                  borderRadius="md"
                  overflow={'hidden'}
                  textAlign="center"
                  cursor="pointer"
                  position="relative"
                  onClick={() => document.getElementById('shop_cover_image_input')?.click()}
                >
                  {shopCoverImage && shopCoverImage instanceof File ? (
                    <Box position="relative" bg={'background.white'} className="group">
                      <Button
                        display={'none'}
                        pos={'absolute'}
                        size={'sm'}
                        fontSize={'sm'}
                        right={'50%'}
                        top={'50%'}
                        transform={'translate(50%, -50%)'}
                        color={'text.white'}
                        bg={'status.error'}
                        onClick={(e) => handleRemoveImage(e, 'shop_cover_image')}
                        _groupHover={{ display: 'block' }}
                        zIndex={1}
                      >
                        Remove
                      </Button>
                      <Image
                        src={shopCoverImagePreview || ''}
                        alt="Shop Cover"
                        width={['100%', '400px']}
                        height={['150px', '200px']}
                        objectFit={'cover'}
                        objectPosition={'center'}
                      />
                    </Box>
                  ) : localStoreData.shop_cover_image && shopCoverImage !== '' ? (
                    <Box position="relative" bg={'background.white'} className="group">
                      <Button
                        display={'none'}
                        pos={'absolute'}
                        size={'sm'}
                        fontSize={'sm'}
                        right={'50%'}
                        top={'50%'}
                        transform={'translate(50%, -50%)'}
                        color={'text.white'}
                        bg={'status.error'}
                        onClick={(e) => handleRemoveImage(e, 'shop_cover_image')}
                        _groupHover={{ display: 'block' }}
                        zIndex={1}
                      >
                        Remove
                      </Button>
                      <Image
                        src={localStoreData.shop_cover_image}
                        alt="Shop Cover"
                        width={['100%', '400px']}
                        height={['150px', '200px']}
                        objectFit={'cover'}
                        objectPosition={'center'}
                      />
                    </Box>
                  ) : (
                    <Flex
                      bg={'background.white'}
                      direction={'column'}
                      gap={1}
                      align={'center'}
                      p={4}
                      minH={'150px'}
                      justify={'center'}
                    >
                      <Icon as={ImageIcon} w={8} h={8} color="gray.500" />
                      <Text
                        textAlign={'center'}
                        mt={2}
                        color="brand.primary.DEFAULT"
                        textDecor={'underline'}
                      >
                        Click to upload
                      </Text>
                      <Text textAlign={'center'} fontSize="sm" color="gray.400">
                        PNG, JPG, WEBP up to 1MB
                      </Text>
                    </Flex>
                  )}
                </Box>
                <Input
                  id="shop_cover_image_input"
                  type="file"
                  display="none"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleImageChange(e, 'shop_cover_image')}
                />
                <FormErrorMessage>{errors.shop_cover_image?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </VStack>
        </FormProvider>
      </CustomModal>

      <Card
        shadow={'none'}
        rounded={'lg'}
        bg={'background.white'}
        borderColor={'border.default60'}
        borderWidth="1px"
      >
        <VStack align="space-between" spacing={0}>
          <Flex p={4} bg={'background.tertiary'} justify="space-between" align="center">
            <Text fontSize="lg" fontWeight={'semibold'}>
              Store Details
            </Text>
            <CustomButton onClick={handleEdit} px={8} fontSize={'sm'} colorScheme="red">
              Edit
            </CustomButton>
          </Flex>

          <Box>
            <Box mb={16} pos={'relative'}>
              {isLoading ? (
                <Skeleton height="400px" width="full" borderRadius="lg" />
              ) : (
                <Image
                  src={displayCoverImage}
                  alt="Store Cover"
                  borderRadius="md"
                  width={'full'}
                  height={'400px'}
                  objectFit="cover"
                  objectPosition={'center'}
                  rounded={0}
                />
              )}
              {isLoading ? (
                <Skeleton
                  borderRadius="full"
                  pos={'absolute'}
                  left={4}
                  bottom={-16}
                  boxSize={'150px'}
                />
              ) : (
                <Image
                  pos="absolute"
                  left={4}
                  bottom={-16}
                  boxSize="150px"
                  objectFit="cover"
                  border="4px solid"
                  borderColor="#fff"
                  borderRadius="full"
                  src={displayShopImage}
                  bg="gray.200"
                  alt="Store Logo"
                />
              )}
            </Box>

            <VStack p={4} align="start" spacing={4}>
              <Flex width="full" justify="space-between" align="start" gap={4}>
                <VStack align="start" spacing={0} flex="1">
                  <Text fontSize="sm" color={'text.secondary'}>
                    Name
                  </Text>
                  {isLoading ? (
                    <Skeleton height="20px" width="150px" />
                  ) : (
                    <Text fontSize="md" color={'text.primary'}>
                      {localStoreData.shop_name || '-'}
                    </Text>
                  )}
                </VStack>

                <VStack align="start" spacing={0} flex="1">
                  <Text fontSize="sm" color={'text.secondary'}>
                    Warehouse Location
                  </Text>
                  {isLoading ? (
                    <Skeleton height="20px" width="150px" />
                  ) : (
                    <Text fontSize="md" color={'text.primary'}>
                      {localStoreData.warehouse_location || '-'}
                    </Text>
                  )}
                </VStack>
              </Flex>

              <VStack pt={4} align="start" spacing={0} width="full">
                <Text fontSize="sm" color={'text.secondary'}>
                  Description
                </Text>
                {isLoading ? (
                  <Skeleton height="20px" width="200px" />
                ) : (
                  <Text fontSize="md" color={'text.primary'}>
                    {localStoreData.description || '-'}
                  </Text>
                )}
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Card>
    </>
  );
};

export default StoreSettings;
